import parseISO from "date-fns/parseISO"
import DEFAULT_I18N from "~/i18n"
import { PluginReviewActionNotAllowedError } from "~/lib/AjaxCtx"
import { ucfirst } from "~/lib/utils"
import { mergeAnswers } from "./pollUtils"

const DEFAULT_USER_ACTIVITY = {
    localState: {},
}

export const mutations = {
    "poll/setAnswer"(state, { userActivityId, questionId, answer }) {
        if (!(userActivityId in state.userActivities)) {
            throw new Error(
                `The activity with id = ${userActivityId} is not defined`
            )
        }

        if (!questionId) {
            questionId = answer.questionId
        }

        const activity = state.userActivities[userActivityId]
        const attemptId = activity.state.currentAttempt.userPollAttemptId
        if (!attemptId) {
            throw new Error(`The attemptId is not defined`)
        }

        if (!activity.localState) {
            activity.localState = {}
        }
        if (!activity.localState.currentUnsavedAttempt) {
            activity.localState.currentUnsavedAttempt = {}
        }

        const attempt = activity.localState.currentUnsavedAttempt

        if (!attempt.userAnswers) {
            attempt.userAnswers = []
        }
        if (!attempt.userPollAttemptId) {
            attempt.userPollAttemptId = attemptId
        }

        const questionIndex = attempt.userAnswers.findIndex(
            a => a.questionId === questionId
        )
        if (questionIndex < 0) {
            attempt.userAnswers.push(answer)
        } else {
            Object.assign(attempt.userAnswers[questionIndex], answer)
        }
    },

    "poll/disableAttempt"(state, { userActivityId }) {
        if (!(userActivityId in state.userActivities)) {
            throw new Error("boom! this activity doesn't exist")
        }

        const activity = state.userActivities[userActivityId]
        activity.state.currentAttempt.isFinished = true
    },

    "poll/createAttemptIfNeeded"(state, { userActivityId, attempt }) {
        const activity = state.userActivities[userActivityId]
        activity.state.currentAttempt = attempt
        if (!activity.localState.currentUnsavedAttempt) {
            activity.localState.currentUnsavedAttempt = {}
        }
        activity.localState.currentUnsavedAttempt.userAnswers = []
    },

    "poll/setCurrentAttemptTimeLeft"(state, { userActivityId, time }) {
        const activity = state.userActivities[userActivityId]
        if (time < 0) {
            time = 0
        }
        activity.localState.currentUnsavedAttempt.timeLeft = time
    },

    "poll/setAttemptTimer"(state, { userActivityId, timer }) {
        const activity = state.userActivities[userActivityId]
        activity.localState.currentUnsavedAttempt.timer = timer
    },

    "poll/setAttemptTimerAlertStatus"(state, { userActivityId, alertWasSent }) {
        const activity = state.userActivities[userActivityId]
        activity.localState.currentUnsavedAttempt.alertWasSent = alertWasSent
    },

    "poll/setPage"(state, { userActivityId, page }) {
        const activity = state.userActivities[userActivityId]
        activity.localState.currentUnsavedAttempt.page = page
    },

    "poll/setExitMessage"(state, { userActivityId, exitMessage }) {
        if (!state.userActivities[userActivityId]) {
            state.userActivities[userActivityId] = DEFAULT_USER_ACTIVITY
        }
        const activity = state.userActivities[userActivityId]
        if (!activity.localState) {
            activity.localState = {}
        }
        if (!activity.localState.currentUnsavedAttempt) {
            activity.localState.currentUnsavedAttempt = {}
        }
        activity.localState.currentUnsavedAttempt.exitMessage = exitMessage
    },
}

export function makeActions(service, i18n) {
    i18n = i18n || DEFAULT_I18N.global
    return {
        "poll/startAttempt"(
            { state, commit, dispatch },
            { userActivityId, attempt }
        ) {
            const activity = state.userActivities[userActivityId]
            commit("poll/createAttemptIfNeeded", {
                userActivityId,
                attempt,
            })
            dispatch("poll/updateExitMessage", {
                userActivityId,
            })
            if (activity.configuration.maxTimeToCompletion) {
                if (activity.state.currentAttempt.isFinished) {
                    commit("poll/setCurrentAttemptTimeLeft", {
                        userActivityId,
                        time: 0,
                    })
                } else {
                    dispatch("poll/startTimer", { userActivityId })
                }
            }
            const page = activity.localState.currentUnsavedAttempt.page || 0
            commit("poll/setPage", { userActivityId, page })
        },

        "poll/startTimer"({ state, dispatch }, { userActivityId }) {
            const activity = state.userActivities[userActivityId]
            const localAttempt = activity.localState.currentUnsavedAttempt
            const timer = localAttempt.timer || null
            if (timer) {
                dispatch("poll/clearAttemptTimer", {
                    userActivityId,
                })
            }

            dispatch("poll/configureCurrentAttemptTimer", {
                userActivityId,
            })
        },

        "poll/configureCurrentAttemptTimer"(
            { state, commit, dispatch },
            { userActivityId }
        ) {
            const activity = state.userActivities[userActivityId]
            const currentFrontTime = Date.now()

            const timer = setInterval(
                () =>
                    tickTimer(
                        { commit, dispatch },
                        { activity, currentFrontTime, i18n }
                    ),
                1000
            )
            commit("poll/setAttemptTimer", {
                userActivityId,
                timer,
            })
        },

        "poll/clearAttemptTimer"({ state }, { userActivityId }) {
            const activity = state.userActivities[userActivityId]
            const timer = activity.localState.currentUnsavedAttempt.timer
            clearInterval(timer)
        },

        "poll/changePage"({ commit }, { userActivityId, page }) {
            commit("poll/setPage", { userActivityId, page })
        },

        "poll/start"({ dispatch }, { userActivityId, attemptId }) {
            return service
                .start(userActivityId, attemptId)
                .then(res => {
                    const attempt = res
                    dispatch("poll/startAttempt", {
                        userActivityId,
                        attempt,
                    })
                })
                .catch(error => {
                    dispatch(
                        "pushNotification",
                        {
                            title: error.message,
                            type: "error",
                            showCloseButton: true,
                        },
                        { root: true }
                    )
                })
        },

        async "poll/correct"(
            { commit, state, dispatch },
            { userActivityId, answers }
        ) {
            const activity = state.userActivities[userActivityId]
            const attemptId = activity.state.currentAttempt.userPollAttemptId

            try {
                const { answers: correctedAnswers } = await service.correct(
                    userActivityId,
                    attemptId,
                    answers
                )
                for (const answer of correctedAnswers) {
                    const questionId = answer.questionId
                    commit("poll/setAnswer", {
                        userActivityId,
                        questionId,
                        answer,
                    })
                }
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "poll/save"({ state, getters, dispatch }, { userActivityId }) {
            const activity = state.userActivities[userActivityId]
            const attemptId = activity.state.currentAttempt.userPollAttemptId
            const answers = getters["poll/getAnswers"]({ userActivityId })
            try {
                await service.save(userActivityId, attemptId, answers)
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                    return
                }
                throw error
            }
        },

        async "poll/finish"(
            { dispatch, commit, state, getters },
            { userActivityId }
        ) {
            const activity = state.userActivities[userActivityId]
            const attemptId = activity.state.currentAttempt.userPollAttemptId
            const answers = getters["poll/getAnswers"]({ userActivityId })
            commit("poll/disableAttempt", {
                userActivityId,
            })

            try {
                const res = await service.finish(
                    userActivityId,
                    attemptId,
                    answers
                )

                for (const answer of res.answers) {
                    const questionId = answer.questionId
                    commit("poll/setAnswer", {
                        userActivityId,
                        questionId,
                        answer,
                    })
                }

                commit("poll/setExitMessage", {
                    userActivityId,
                    exitMessage: "",
                })
            } catch (error) {
                if (error instanceof PluginReviewActionNotAllowedError) {
                    dispatch("lms/refreshUserActivity", userActivityId, {
                        root: true,
                    })
                }
                dispatch("poll/clearAttemptTimer", {
                    userActivityId,
                })

                commit("poll/setExitMessage", {
                    userActivityId,
                    exitMessage: "",
                })
            }
        },

        "poll/updateExitMessage"(
            { commit, state, getters },
            { userActivityId }
        ) {
            const activity = state.userActivities[userActivityId]
            const attempt = activity.state.currentAttempt
            const attemptIsFinished = attempt?.isFinished ?? false
            const attemptIsStarted = !!attempt?.attempt
            const answers = getters["poll/getAnswers"]({ userActivityId })
            const answeredQuestions = answers.filter(
                a => a.answer !== null
            ).length
            const totalQuestions = attemptIsStarted
                ? attempt.totalQuestions
                : activity.state.totalQuestions
            const pendingQuestions = totalQuestions - answeredQuestions
            if (attemptIsFinished || !attemptIsStarted) {
                commit("poll/setExitMessage", {
                    userActivityId,
                    exitMessage: "",
                })
            } else if (pendingQuestions > 0) {
                commit("poll/setExitMessage", {
                    userActivityId,
                    exitMessage: ucfirst(i18n.t("common.wantToSaveAnswers")),
                })
            } else {
                commit("poll/setExitMessage", {
                    userActivityId,
                    exitMessage: ucfirst(i18n.t("common.rememberFinishPoll")),
                })
            }
        },
    }
}

export const getters = {
    "poll/getAnswers":
        state =>
        ({ userActivityId }) => {
            if (!state.userActivities[userActivityId]) {
                throw new Error(`Error: not defined activity ${userActivityId}`)
            }
            const activity = state.userActivities[userActivityId]
            if (!activity.state.currentAttempt) {
                return []
            }
            const localAttempt = activity.localState.currentUnsavedAttempt || {}

            const remoteAttempt = activity.state.currentAttempt

            if (!remoteAttempt || remoteAttempt.length === 0) return []

            return mergeAnswers(
                remoteAttempt.userAnswers,
                localAttempt.userAnswers
            )
        },
}

function tickTimer({ commit, dispatch }, { activity, currentFrontTime, i18n }) {
    const userActivityId = activity.userActivityId

    if (activity.state.currentAttempt.isFinished) {
        dispatch("poll/clearAttemptTimer", {
            userActivityId,
        })
        return
    }

    const alarmMessage = ucfirst(i18n.t("common.timeAlarm"))
    const timeIsOverMessage = ucfirst(i18n.t("common.timeIsOver"))
    const MILLISECONDS_PER_MINUTE = 60 * 1000
    const localAttempt = activity.localState.currentUnsavedAttempt
    const maxTimeToCompletion =
        activity.configuration.maxTimeToCompletion * MILLISECONDS_PER_MINUTE
    const startTime = parseISO(activity.state.currentAttempt.startAt).getTime()
    const frontNowTime = Date.now()
    const currentBackTime = parseISO(
        activity.state.currentAttempt.currentTime
    ).getTime()
    const timeLeft =
        startTime +
        maxTimeToCompletion +
        currentFrontTime -
        currentBackTime -
        frontNowTime

    commit("poll/setCurrentAttemptTimeLeft", {
        userActivityId,
        time: timeLeft / 1000,
    })

    if (localAttempt.timeLeft <= 0) {
        dispatch(
            "pushNotification",
            {
                title: timeIsOverMessage,
                type: "warning",
            },
            { root: true }
        )
        dispatch("poll/finish", { userActivityId })
    } else if (localAttempt.timeLeft <= 60 && !localAttempt.alertWasSent) {
        commit("poll/setAttemptTimerAlertStatus", {
            userActivityId,
            alertWasSent: true,
        })

        dispatch(
            "pushNotification",
            {
                title: alarmMessage,
                type: "warning",
            },
            { root: true }
        )
    }
}

export default {
    getters,
    mutations,
    makeActions,
}
